<template>
  <div class="hotel-search-form">
    <!-- Location Field with Google Places Autocomplete -->
    <div class="field">
      <div class="location-input-group">
        <div class="search-location-field">
          <b-field :type="errors.destination ? 'is-danger' : ''" :message="errors.destination">
            <b-input
              v-model="destination"
              placeholder="Where to?"
              icon="map-marker"
              @blur="validateDestination"
              ref="locationInput"
              expanded
            />
          </b-field>
        </div>

        <!-- Saved Locations Dropdown -->
        <div v-if="preferredLocations.length > 0" class="saved-addresses-select">
          <div class="dropdown" :class="{ 'is-active': isDropdownOpen }">
            <div class="dropdown-trigger">
              <button
                class="button saved-address-button"
                aria-haspopup="true"
                aria-controls="dropdown-menu"
                @click="toggleDropdown"
                :disabled="isLoadingPreferredLocations && preferredLocations.length === 0"
              >
                <span class="icon is-small">
                  <i v-if="isLoadingPreferredLocations && preferredLocations.length === 0" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                  <i v-else class="fas fa-bookmark" aria-hidden="true"></i>
                </span>
                <span>{{ isLoadingPreferredLocations && preferredLocations.length === 0 ? 'Loading...' : 'Saved Locations' }}</span>
                <span v-if="!isLoadingPreferredLocations || preferredLocations.length > 0" class="icon is-small">
                  <i class="fas" :class="isDropdownOpen ? 'fa-chevron-up' : 'fa-chevron-down'" aria-hidden="true"></i>
                </span>
              </button>
            </div>
            <div class="dropdown-menu" id="dropdown-menu" role="menu" v-if="!isLoadingPreferredLocations || preferredLocations.length > 0">
              <div class="dropdown-content">
                <div v-if="preferredLocations.length === 0 && !isLoadingPreferredLocations" class="dropdown-item">
                  <span class="has-text-grey">No saved addresses</span>
                </div>
                <a
                  v-for="(address, index) in preferredLocations"
                  :key="index"
                  class="dropdown-item"
                  @click="handleSavedAddressSelect(address)"
                >
                  <div class="saved-address-option">
                    <span class="saved-address-name">{{ address.name }}</span>
                    <span class="saved-address-details">{{ address.address }}</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Date Range Field -->
    <div class="field">
      <b-field :type="errors.searchDates ? 'is-danger' : ''" :message="errors.searchDates">
        <DateSelectorRange
          segmentType="hotel"
          placeholder="Check-in / check-out"
          :minDate="minDate"
          v-model="searchDates"
          icon="calendar-alt"
          icon-pack="fas"
          @blur="validateSearchDates"
        />
      </b-field>
    </div>

    <!-- Rate Types -->
    <div class="field is-size-7 mb-5">
      <b-field label="Rate to include" grouped>
        <b-checkbox
          v-for="rate in hotelRateOptions"
          :key="rate.value"
          v-model="selectedRates"
          :native-value="rate.value"
          size="is-small"
        >
          {{ rate.text }}
        </b-checkbox>
      </b-field>
    </div>

    <!-- Search Button -->
    <div class="field mt-5">
      <b-button 
        type="is-primary" 
        expanded 
        @click="handleSearch"
        :loading="searching"
        :disabled="!isFormValid"
      >
        <b-icon icon="magnify" class="mr-2"></b-icon>
        Search Hotels
      </b-button>
    </div>
  </div>
</template>

<style scoped>
.location-input-group {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  max-width: 600px;
}

.search-location-field {
  flex: 1;
  margin-bottom: 0.5rem;
}

/* Saved addresses styles */
.saved-addresses-select {
  width: auto;
}

.saved-address-button {
  height: 2.75em;
  padding: 0 1rem;
  background-color: #e9ecef;
  border: 1px solid #dee2e6;
  color: #495057;
  border-radius: 4px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.saved-address-button:hover:not(:disabled) {
  background-color: #dee2e6;
  border-color: #ced4da;
}

.saved-address-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.saved-address-button .icon {
  color: #6c757d;
}

.saved-addresses-select .dropdown-menu {
  width: 300px !important;
  min-width: 300px !important;
  max-width: 300px !important;
  margin-top: 0.25rem;
  right: 0;
  left: auto;
}

.saved-addresses-select .dropdown-content {
  width: 100%;
  padding: 0.5rem 0;
  max-width: 300px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.saved-addresses-select .dropdown-item {
  width: 100%;
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  white-space: normal;
  word-wrap: break-word;
  transition: background-color 0.2s ease;
}

.saved-addresses-select .dropdown-item:last-child {
  border-bottom: none;
}

.saved-addresses-select .dropdown-item:hover {
  background-color: #f8f9fa;
}

.saved-address-option {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
}

.saved-address-name {
  font-weight: 500;
  color: #363636;
  font-size: 0.9rem;
  white-space: normal;
  word-wrap: break-word;
}

.saved-address-details {
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.2;
  white-space: normal;
  word-wrap: break-word;
}
</style>

<script>
import DateSelectorRange from '../DateComponents/DateSelectorRange'

export default {
  name: 'HotelSearchForm',
  components: {
    DateSelectorRange
  },
  data() {
    return {
      destination: '',
      searchDates: [],
      selectedRates: [],
      searching: false,
      errors: {},
      googlePlace: null,
      preferredLocations: [],
      isLoadingPreferredLocations: false,
      isDropdownOpen: false,
      hotelRateOptions: [
        { text: "Government", value: "GOV" },
        { text: "Military", value: "MIL" },
        { text: "Senior", value: "SRS" },
        { text: "AAA", value: "AAA" },
      ]
    }
  },
  computed: {
    minDate() {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return today
    },
    isFormValid() {
      return this.destination.trim() &&
             this.searchDates &&
             this.searchDates.length === 2 &&
             this.searchDates[0] &&
             this.searchDates[1] &&
             Object.keys(this.errors).length === 0
    },
    currentTravellerProfile() {
      return this.$store.getters['travellersAndTrips/getCurrentTravellerProfile']
    },
    checkinDate() {
      return this.searchDates && this.searchDates.length > 0 ? this.searchDates[0] : null
    },
    checkoutDate() {
      return this.searchDates && this.searchDates.length > 1 ? this.searchDates[1] : null
    },
    profile() {
      return this.$store.state.travellersAndTrips.currentTravellerProfile
    }
  },
  methods: {
    async initializeGooglePlaces() {
      try {
        const Library = await this.$store.dispatch("hotelSearch/getPlacesLibrary")
        const input = this.$refs.locationInput.$el.querySelector('input')

        if (input) {
          const options = {
            types: ['(cities)'],
            fields: ['name', 'geometry', 'formatted_address']
          }

          const autocomplete = new Library.Autocomplete(input, options)

          autocomplete.addListener("place_changed", () => {
            this.googlePlace = autocomplete.getPlace()
            if (this.googlePlace && this.googlePlace.geometry) {
              this.destination = this.googlePlace.name || this.googlePlace.formatted_address
              this.validateDestination()
            }
          })
        }
      } catch (error) {
        console.error('Error initializing Google Places:', error)
      }
    },

    async loadSavedLocations() {
      if (!this.currentTravellerProfile?.account?.id) return

      this.isLoadingPreferredLocations = true
      try {
        const data = { accountId: this.currentTravellerProfile.account.id }
        const response = await this.$app.huddleLabsServerCall("profile/account", data)

        if (response.data?.savedLocations) {
          this.preferredLocations = response.data.savedLocations
        }
      } catch (error) {
        console.error('Error loading saved locations:', error)
      } finally {
        this.isLoadingPreferredLocations = false
      }
    },

    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },

    handleSavedAddressSelect(address) {
      this.isDropdownOpen = false
      if (address) {
        this.destination = address.name
        this.googlePlace = {
          name: address.name,
          geometry: {
            location: {
              lat: () => address.latitude,
              lng: () => address.longitude
            }
          }
        }
        this.validateDestination()
      }
    },

    validateDestination() {
      if (!this.destination.trim()) {
        this.$set(this.errors, 'destination', 'Please enter a destination')
      } else {
        this.$delete(this.errors, 'destination')
      }
    },
    validateSearchDates() {
      if (!this.searchDates || this.searchDates.length !== 2) {
        this.$set(this.errors, 'searchDates', 'Please select check-in and check-out dates')
      } else if (!this.searchDates[0] || !this.searchDates[1]) {
        this.$set(this.errors, 'searchDates', 'Please select both check-in and check-out dates')
      } else {
        // Normalize dates for comparison by setting time to start of day
        const checkinDate = new Date(this.searchDates[0])
        checkinDate.setHours(0, 0, 0, 0)
        const checkoutDate = new Date(this.searchDates[1])
        checkoutDate.setHours(0, 0, 0, 0)
        const minDate = new Date(this.minDate)
        minDate.setHours(0, 0, 0, 0)

        if (checkinDate < minDate) {
          this.$set(this.errors, 'searchDates', 'Please select a future date')
        } else if (checkoutDate <= checkinDate) {
          this.$set(this.errors, 'searchDates', 'Check-out date must be after check-in date')
        } else {
          this.$delete(this.errors, 'searchDates')
        }
      }
    },
    handleSearch() {
      // Validate all fields
      this.validateDestination()
      this.validateSearchDates()

      if (this.isFormValid) {
        this.searching = true

        // Set default traveller settings for business travel
        this.$store.commit('travellersAndTrips/EDIT_TRAVELLERS', {
          tripType: 'business',
          bookFor: 'user',
          set: true
        })

        const searchData = {
          destination: this.destination,
          checkinDate: this.checkinDate,
          checkoutDate: this.checkoutDate,
          selectedRates: this.selectedRates,
          googlePlace: this.googlePlace
        }

        this.$emit('search', searchData)

        // Reset searching state after a delay
        setTimeout(() => {
          this.searching = false
        }, 1000)
      }
    }
  },

  watch: {
    'profile.eligibleForTheFollowingDiscounts': {
      handler(val) {
        // Auto-select rates based on user profile eligibility
        if (val && Array.isArray(val)) {
          this.selectedRates = val
        }
      },
      immediate: true,
    },
  },

  created() {
    // Initialize selectedRates from profile if available
    if (this.profile && this.profile.eligibleForTheFollowingDiscounts) {
      this.selectedRates = Array.isArray(this.profile.eligibleForTheFollowingDiscounts)
        ? this.profile.eligibleForTheFollowingDiscounts
        : []
    }
  },

  async mounted() {
    await this.initializeGooglePlaces()
    await this.loadSavedLocations()
  }
}
</script>

<style scoped>
.advanced-options {
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .columns {
    margin: 0;
    display: block;
  }

  .column {
    padding: 0.5rem 0;
    width: 100% !important;
  }

  .advanced-options {
    padding-top: 0.75rem;
  }

  /* Stack location input and saved locations on separate rows on mobile */
  .location-input-group {
    flex-direction: column;
    gap: 0.75rem;
    max-width: none;
    align-items: stretch;
  }

  .search-location-field {
    flex: none;
    margin-bottom: 0;
    width: 100%;
  }

  .saved-addresses-select {
    flex: none;
    width: 100%;
  }

  .saved-addresses-select .dropdown {
    width: 100%;
  }

  .saved-addresses-select .dropdown-trigger {
    width: 100%;
  }

  .saved-address-button {
    height: 2.75em;
    padding: 0 1rem;
    font-size: 0.9rem;
    white-space: nowrap;
    width: 100% !important;
    justify-content: center;
    display: flex !important;
  }

  /* Show "Saved locations" label on mobile */
  .saved-address-button span:not(.icon):not(:last-child) {
    display: inline;
  }

  .saved-addresses-select .dropdown-menu {
    width: 280px !important;
    min-width: 280px !important;
    max-width: 280px !important;
    right: 0;
    left: auto;
  }

  .saved-addresses-select .dropdown-content {
    max-width: 280px;
  }

  /* Ensure all form controls are full width and same height */
  .field .control,
  .field .control .input,
  .field .control .datepicker {
    width: 100%;
  }

  .field .control .input {
    height: 2.5em;
  }
}
</style>
